import { ChevronUp, RotateCcw } from "feather-icons-react/build/IconComponents";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { setToogleHeader } from "../../core/redux/action";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { message } from "antd";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
  AllCommunityModule,
  ModuleRegistry,
  provideGlobalGridOptions,
} from "ag-grid-community";
import { getCurrentUser } from "../../core/api/settings";
import { fetchTigerTechSales, syncTigerTechSales } from "../../core/api/data";
import formatDateWithTimezone from "../../utils/dateUtils";
import { dateFilterParams } from "../../utils/dateUtils";

ModuleRegistry.registerModules([AllCommunityModule]);
provideGlobalGridOptions({ theme: "legacy" });

const SocrateOrders = ({ timezone, filters, refreshTrigger }) => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  useEffect(() => {
    if (filters && filters.brands && filters.brands.length > 0) {
      setLoading(true);
      fetchOrdersData(filters.brands[0], filters);
    } else {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (refreshTrigger > 0 && filters && filters.brands && filters.brands.length > 0) {
      setLoading(true);
      fetchOrdersData(filters.brands[0], filters);
    }
  }, [refreshTrigger]);

  
  const fetchOrdersData = (brandId, currentFilters = null) => {
    setLoading(true);
    
    // Add source parameter for Socrate
    const fetchPromise = currentFilters
      ? fetchTigerTechSales(brandId, {...currentFilters, source: 'Socrate'})
      : fetchTigerTechSales(brandId, {source: 'Socrate'});
      
    fetchPromise
      .then((data) => {
        setRowData(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching Socrate orders:", error);
        message.error("Failed to fetch Socrate orders");
        setLoading(false);
      });
  };

  const handleSyncSocrate = () => {
    if (!filters || !filters.brands || filters.brands.length === 0) {
      message.error("Please select a brand first");
      return;
    }

    setSyncInProgress(true);
    message.loading({ content: "Syncing Socrate orders...", key: "socrateSync", duration: 0 });

    // Add source parameter for Socrate
    syncTigerTechSales(filters.brands[0], 'Socrate')
      .then((response) => {
        if (response.error) {
          message.error({ content: `Sync failed: ${response.error}`, key: "socrateSync" });
        } else {
          message.success({ 
            content: `Sync completed. Imported: ${response.imported}, Skipped: ${response.skipped}, Errors: ${response.errors}`, 
            key: "socrateSync",
            duration: 5
          });
          
          // Refresh data after successful sync
          fetchOrdersData(filters.brands[0], filters);
          
          // Update last sync time
          setLastSyncTime(new Date().toLocaleTimeString());
        }
      })
      .catch((error) => {
        console.error("Error syncing Socrate orders:", error);
        message.error({ 
          content: `Sync failed: ${error.message || "Unknown error"}`, 
          key: "socrateSync" 
        });
      })
      .finally(() => {
        setSyncInProgress(false);
      });
  };

  // Column definitions for AG Grid
  const columns = [
    {
      headerName: "Order Number",
      field: "order_number",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Order Date",
      field: "order_date",
      sortable: true,
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
      valueFormatter: (params) => formatDateWithTimezone(params.value, timezone),
      width: 180,
      resizable: true,
    },
    {
      headerName: "Customer",
      field: "bill_first_name",
      valueGetter: (params) => {
        return `${params.data.bill_first_name || ''} ${params.data.bill_last_name || ''}`.trim();
      },
      sortable: true,
      filter: true,
      width: 180,
      resizable: true,
    },
    {
      headerName: "Email",
      field: "bill_email",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Phone",
      field: "bill_phone_number",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Address",
      field: "bill_address1",
      valueGetter: (params) => {
        let address = params.data.bill_address1 || '';
        if (params.data.bill_address2) {
          address += `, ${params.data.bill_address2}`;
        }
        if (params.data.bill_city) {
          address += `, ${params.data.bill_city}`;
        }
        if (params.data.bill_state) {
          address += `, ${params.data.bill_state}`;
        }
        if (params.data.bill_zipcode) {
          address += ` ${params.data.bill_zipcode}`;
        }
        return address;
      },
      sortable: true,
      filter: true,
      width: 300,
      resizable: true,
    },
    {
      headerName: "Payment Type",
      field: "payment_type",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Total",
      field: "order_total",
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        return params.value ? `$${parseFloat(params.value).toFixed(2)}` : '';
      },
      width: 120,
      resizable: true,
    },
    {
      headerName: "Product 1",
      field: "product01",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Qty 1",
      field: "quantity01",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Product 2",
      field: "product02",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Qty 2",
      field: "quantity02",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Product 3",
      field: "product03",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Qty 3",
      field: "quantity03",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Product 4",
      field: "product04",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Qty 4",
      field: "quantity04",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Product 5",
      field: "product05",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Qty 5",
      field: "quantity05",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Product 6",
      field: "product06",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Qty 6",
      field: "quantity06",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Product 7",
      field: "product07",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Qty 7",
      field: "quantity07",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Product 8",
      field: "product08",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Qty 8",
      field: "quantity08",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Product 9",
      field: "product09",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Qty 9",
      field: "quantity09",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Product 10",
      field: "product10",
      sortable: true,
      filter: true,
      width: 200,
      resizable: true,
    },
    {
      headerName: "Qty 10",
      field: "quantity10",
      sortable: true,
      filter: true,
      width: 100,
      resizable: true,
    },
    {
      headerName: "Shipping Method",
      field: "shipping_method",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Tracking Number",
      field: "tracking_num",
      sortable: true,
      filter: true,
      width: 180,
      resizable: true,
    },
    {
      headerName: "Shipping Carrier",
      field: "shipping_carrier",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Shipping Date",
      field: "shipping_date",
      sortable: true,
      filter: true,
      width: 150,
      resizable: true,
    },
    {
      headerName: "Custom 1",
      field: "custom_1",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Custom 2",
      field: "custom_2",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Custom 3",
      field: "custom_3",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Custom 4",
      field: "custom_4",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
    {
      headerName: "Custom 5",
      field: "custom_5",
      sortable: true,
      filter: true,
      width: 120,
      resizable: true,
    },
  ];

  return (
    <div className="">
      <div className="">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Socrate Orders</h4>
            </div>
          </div>

          <ul className="table-top-head">
            <li>
            {lastSyncTime && (
                <p className="text-muted">Last synced: {lastSyncTime}</p>
              )}
            </li>
              <li>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>PDF</Tooltip>}
                >
                  <Link
                    onClick={() => {
                      // PDF export functionality would go here
                    }}
                  >
                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
            
              <li>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Excel</Tooltip>}
                >
                  <Link
                    onClick={() => {
                      // Excel export functionality would go here
                    }}
                  >
                    <ImageWithBasePath src="assets/img/icons/xls.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
            <li>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Collapse</Tooltip>}
              >
                <Link
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => dispatch(setToogleHeader(!data))}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
          <div className="page-btn">
              <button 
                className="btn btn-added" 
                onClick={handleSyncSocrate}
                disabled={syncInProgress || !filters || !filters.brands || filters.brands.length === 0}
              >
                <RotateCcw className="me-2 iconsize" />
                Sync Socrate Orders
              </button>
          </div>
        </div>

        {/* Ag-Grid Table */}
        <div className="card table-list-card">
          <div
            className="ag-theme-quartz"
            style={{ height: "700px", width: "100%", overflow: "auto" }}
          >
            <AgGridReact
              columnDefs={columns}
              rowData={rowData}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
                minWidth: 80
              }}
              pagination={false}
              loading={loading}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              suppressColumnVirtualisation={false}
              suppressRowVirtualisation={false}
              suppressHorizontalScroll={false}
              domLayout="normal"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocrateOrders;
